import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI, deleteDataAPI } from '../../utils/fetchData';
import { useDispatch } from 'react-redux';
import { setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import Modal from '../../components/Modal';
import { useModal } from '../../hooks/useModal';

const ContactsApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { modalConfig, showModal, closeModal } = useModal();

  const [contacts, setContacts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);


  useEffect(() => {
    loadContacts();
    loadRoles();
  }, [activeTab, searchQuery]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const params = new URLSearchParams();
      
      if (activeTab !== 'all') {
        params.append('role', activeTab);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const response = await getDataAPI(`user/contacts?${params.toString()}`, token);
      
      if (response.data.status === 200) {
        setContacts(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      dispatch(setErrorAlert('Failed to load contacts'));
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/contacts/roles', token);
      
      if (response.data.status === 200) {
        setRoles(response.data.data.roles || []);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };




  const handleDeleteContact = (contact) => {
    showModal(
      'Delete Contact',
      `Are you sure you want to delete ${contact.name}?`,
      'warning',
      async () => {
        try {
          const token = JSON.parse(localStorage.getItem('dizeeToken'));
          await deleteDataAPI(`user/contacts/${contact._id}`, {}, token);
          dispatch(setSuccessAlert('Contact deleted successfully'));
          loadContacts();
          closeModal();
        } catch (error) {
          console.error('Error deleting contact:', error);
          dispatch(setErrorAlert('Failed to delete contact'));
        }
      },
      true
    );
  };


  const getRoleColor = (role) => {
    const colors = {
      'Guest': 'bg-purple-500/20 text-purple-400',
      'Manager': 'bg-blue-500/20 text-blue-400',
      'Tour Manager': 'bg-cyan-500/20 text-cyan-400',
      'FOH Engineer': 'bg-green-500/20 text-green-400',
      'Monitor Engineer': 'bg-emerald-500/20 text-emerald-400',
      'Production Manager': 'bg-orange-500/20 text-orange-400',
      'Support Act': 'bg-pink-500/20 text-pink-400',
    };
    return colors[role] || 'bg-gray-500/20 text-gray-400';
  };

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'Guest', label: 'Guests' },
    { id: 'Manager', label: 'Managers' },
    { id: 'Tour Manager', label: 'Crew' },
    { id: 'FOH Engineer', label: 'Engineers' },
  ];

  const filteredContacts = contacts;

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Contacts</span>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setShowSearch(!showSearch)}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search Contacts
              </button>
              <button
                onClick={() => navigate('/contacts/add')}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70"
              >
                Add
              </button>
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate(-1)}
              >
                Go back
              </span>
            </div>
          </div>

          {/* Search Interface */}
          {showSearch && (
            <div className="w-full px-4 mb-6">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#af51de]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span className="text-white text-[12px] font-normal">Search Contacts</span>
                  </div>
                  <button
                    onClick={() => setShowSearch(false)}
                    className="text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
                    title="Close search"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Enter name, company, or role..."
                    className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#af51de]"
                  />
                  
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="w-full mb-6">
            <div className="flex">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#af51de]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="text-[rgba(255,255,255,0.5)] text-[12px]">Loading contacts...</div>
              </div>
            ) : filteredContacts.length === 0 ? (
              <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-semibold mb-2">
                  No {activeTab !== 'all' ? activeTab.toLowerCase() : ''} contacts found
                </h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                  {searchQuery 
                    ? 'Try a different search'
                    : 'Create contacts to manage your network'
                  }
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => navigate('/contacts/add')}
                    className="bg-[#af51de] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#9d40cd] transition-colors"
                  >
                    Create Contact
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3 pb-6">
                {filteredContacts.map((contact) => (
                  <div
                    key={contact._id}
                    className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:bg-[rgba(255,255,255,0.08)] transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-white text-[14px] font-medium mb-1">
                          {contact.name}
                        </h3>
                        <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-2">
                          {contact.company && `${contact.company} • `}{contact.primaryRole}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDeleteContact(contact)}
                          className="text-red-400 hover:opacity-70 text-[11px]"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Roles */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {contact.roles?.slice(0, 3).map((role, idx) => (
                        <span
                          key={idx}
                          className={`px-2 py-1 rounded text-[9px] font-medium ${getRoleColor(role)}`}
                        >
                          {role}
                        </span>
                      ))}
                      {contact.roles?.length > 3 && (
                        <span className="px-2 py-1 rounded text-[9px] font-medium bg-gray-500/20 text-gray-400">
                          +{contact.roles.length - 3}
                        </span>
                      )}
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-1">
                      {contact.contactInfo?.email && (
                        <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                          📧 {contact.contactInfo.email}
                        </p>
                      )}
                      {contact.contactInfo?.phone && (
                        <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                          📱 {contact.contactInfo.phone}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>


      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        showCancel={modalConfig.showCancel}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
    </LayoutHeader>
  );
};

export default ContactsApp;

